/**
 * Size Selector Component
 * Segmented control for selecting RSVP size: S, M, L, or Skip
 */

import { cn } from '@/lib/utils';
import { RSVP_SIZES, RSVP_SIZE_LABELS, type RSVPSizeCode } from '@/types/units';

export type RSVPSize = RSVPSizeCode | 'SKIP' | null;

interface SizeSelectorProps {
  value: RSVPSize;
  onChange: (size: RSVPSize) => void;
  disabled?: boolean;
  includeSkip?: boolean;
  className?: string;
}

/**
 * SizeSelector - Segmented control for thaali size selection
 *
 * @example
 * <SizeSelector
 *   value={selection}
 *   onChange={setSelection}
 * />
 */
export function SizeSelector({
  value,
  onChange,
  disabled = false,
  includeSkip = true,
  className,
}: SizeSelectorProps) {
  const handleClick = (size: RSVPSize) => {
    if (!disabled) {
      // If clicking the same button, toggle off (set to null)
      // But for Skip, just set it directly
      if (size === value && size !== 'SKIP') {
        onChange(null);
      } else {
        onChange(size);
      }
    }
  };

  const options = includeSkip
    ? ([...RSVP_SIZES, 'SKIP'] as const)
    : RSVP_SIZES;

  const getLabel = (size: (typeof options)[number]) => (
    size === 'SKIP' ? 'Skip' : size
  );

  const getAriaLabel = (size: (typeof options)[number]) => (
    size === 'SKIP'
      ? 'Skip this meal'
      : `Select ${RSVP_SIZE_LABELS[size].toLowerCase()} size`
  );

  const gridTemplateColumns = options
    .map((size) => {
      if (size === 'SKIP') return 'minmax(3rem, 1fr)';
      return 'minmax(1.875rem, 1fr)';
    })
    .join(' ');

  return (
    <div
      role="group"
      aria-label="RSVP size"
      data-state={disabled ? 'disabled' : 'enabled'}
      className={cn(
        'grid w-fit max-w-full gap-1 rounded-2xl border p-1 transition-colors',
        disabled
          ? 'border-muted bg-muted/20'
          : 'border-primary/35 bg-primary/5 shadow-sm ring-1 ring-primary/10',
        className
      )}
      style={{ gridTemplateColumns }}
    >
      {options.map((size) => (
        <button
          key={size}
          type="button"
          onClick={() => handleClick(size)}
          disabled={disabled}
          className={cn(
            'flex h-9 min-w-0 items-center justify-center rounded-xl border border-transparent px-2 text-center text-sm font-semibold transition-all',
            'outline-none focus-visible:z-10 focus-visible:ring-2 focus-visible:ring-ring/50 disabled:pointer-events-none',
            disabled
              ? value === size
                ? 'border-primary/30 bg-primary text-primary-foreground shadow-none'
                : 'text-muted-foreground'
              : value === size
                ? 'border-primary/30 bg-primary text-primary-foreground shadow-[inset_0_1px_0_rgb(255_255_255_/_0.22),0_1px_0_rgb(0_0_0_/_0.25),0_4px_8px_rgb(0_0_0_/_0.14)] active:shadow-[inset_0_1px_2px_rgb(0_0_0_/_0.18),0_1px_0_rgb(255_255_255_/_0.08)]'
                : 'text-foreground/75 hover:border-primary/20 hover:bg-background hover:text-foreground',
            size === 'SKIP' && 'min-w-12'
          )}
          aria-label={getAriaLabel(size)}
          aria-pressed={value === size}
        >
          {getLabel(size)}
        </button>
      ))}
    </div>
  );
}
